<script setup lang="ts">
import { computed, onBeforeUnmount, reactive, ref, watch } from "vue";
import { api } from "../api";
import type { ExerciseFeedback, RoutineBlock, RoutineItem, SetEntry, TodayPayload } from "../shared";

type ResistanceKind = "external_load" | "band" | "bodyweight" | "assistance" | "unknown";
type Draft = { weight: string; reps: string; duration: string; rir: string; resistanceKind: ResistanceKind; saved: boolean; dirty: boolean; busy: boolean };
type ExerciseNoteDraft = { value: string; baseline: string; revision: number; dirty: boolean; busy: boolean };
type ActiveTimer = {
  kind: "exercise" | "rest";
  startsAt: number;
  endsAt: number;
  remaining: number;
  leadInRemaining: number;
  block?: RoutineBlock;
  item?: RoutineItem;
  round?: number;
  duration?: number;
};
const TIMER_LEAD_IN_SECONDS = 3;
const props = defineProps<{ payload: TodayPayload | null; loading: boolean }>();
const emit = defineEmits<{ changed: []; error: [message: string]; toast: [message: string] }>();
const drafts = reactive<Record<string, Draft>>({});
const exerciseNoteDrafts = reactive<Record<string, ExerciseNoteDraft>>({});
const noteEditorItem = ref<RoutineItem | null>(null);
const starting = ref(false);
const finishOpen = ref(false);
const abandonOpen = ref(false);
const finishing = ref(false);
const abandoning = ref(false);
const effort = ref(7);
const notes = ref("");
const activeTimer = ref<ActiveTimer | null>(null);
let timer: number | undefined;
let audioContext: AudioContext | null = null;

const day = computed(() => props.payload?.day ?? null);
const session = computed(() => props.payload?.session ?? null);
const dateLabel = computed(() => day.value ? new Intl.DateTimeFormat(undefined, { weekday: "long", month: "short", day: "numeric" }).format(new Date(`${day.value.day_date}T12:00:00`)) : "");
const totalSets = computed(() => day.value?.blocks.reduce((sum, block) => sum + block.items.reduce((itemSum, item) => itemSum + item.target_sets, 0), 0) ?? 0);
const completedSets = computed(() => session.value?.sets.length ?? 0);
const progress = computed(() => totalSets.value ? Math.min(100, (completedSets.value / totalSets.value) * 100) : 0);
const detached = computed(() => Boolean(session.value && !day.value));

function draftKey(itemId: string, round: number) { return `${itemId}:${round}`; }
function savedSet(itemId: string, round: number): SetEntry | undefined { return session.value?.sets.find((set) => set.routine_item_id === itemId && set.round_number === round); }
function ensureDraft(item: RoutineItem, round: number): Draft {
  const key = draftKey(item.id, round);
  return drafts[key] ?? { weight: "", reps: "", duration: "", rir: "", resistanceKind: "unknown", saved: false, dirty: false, busy: false };
}

function savedExerciseNote(itemId: string): ExerciseFeedback | undefined {
  return session.value?.exercise_notes?.find((note) => note.routine_item_id === itemId);
}

function normalizedExerciseNote(value: string): string | null {
  return value.trim() ? value : null;
}

function ensureExerciseNoteDraft(item: RoutineItem): ExerciseNoteDraft {
  const existing = exerciseNoteDrafts[item.id];
  if (existing) return existing;
  const saved = savedExerciseNote(item.id);
  return exerciseNoteDrafts[item.id] = {
    value: saved?.notes ?? "",
    baseline: saved?.notes ?? "",
    revision: saved?.revision ?? 0,
    dirty: false,
    busy: false
  };
}

function syncExerciseNoteDraftsFromPayload() {
  if (!session.value || !day.value) return;
  for (const block of day.value.blocks) for (const item of block.items) {
    const saved = savedExerciseNote(item.id);
    const existing = exerciseNoteDrafts[item.id];
    if (!existing) {
      ensureExerciseNoteDraft(item);
    } else if (!existing.dirty && !existing.busy) {
      existing.value = saved?.notes ?? "";
      existing.baseline = saved?.notes ?? "";
      existing.revision = saved?.revision ?? 0;
    }
  }
}

function syncDraftsFromPayload() {
  if (!day.value) return;
  for (const block of day.value.blocks) for (const item of block.items) for (let round = 1; round <= item.target_sets; round += 1) {
    const key = draftKey(item.id, round);
    const saved = savedSet(item.id, round);
    const existing = drafts[key];
    if (!existing) {
      const plannedReps = item.duration_seconds === null ? item.rep_min ?? item.rep_max : null;
      drafts[key] = {
        weight: saved?.weight?.toString() ?? item.target_weight?.toString() ?? item.previous?.weight?.toString() ?? "",
        reps: saved ? saved.reps?.toString() ?? "" : plannedReps?.toString() ?? "",
        duration: saved?.duration_seconds?.toString() ?? item.duration_seconds?.toString() ?? "",
        rir: saved?.rir?.toString() ?? item.target_rir?.toString() ?? "2",
        resistanceKind: saved?.resistance_kind ?? "unknown", saved: Boolean(saved), dirty: false, busy: false
      };
    } else if (saved && !existing.dirty) {
      existing.weight = saved.weight?.toString() ?? "";
      existing.reps = saved.reps?.toString() ?? "";
      existing.duration = saved.duration_seconds?.toString() ?? "";
      existing.rir = saved.rir?.toString() ?? "";
      existing.resistanceKind = saved.resistance_kind ?? "unknown";
      existing.saved = true;
    }
  }
  syncExerciseNoteDraftsFromPayload();
}

watch(() => props.payload, syncDraftsFromPayload, { immediate: true, deep: true });

watch(() => props.payload?.session?.id ?? null, (next, previous) => {
  if (next === previous) return;
  for (const key of Object.keys(drafts)) delete drafts[key];
  for (const key of Object.keys(exerciseNoteDrafts)) delete exerciseNoteDrafts[key];
  noteEditorItem.value = null;
  clearTimer();
  finishOpen.value = false;
  abandonOpen.value = false;
  if (day.value) {
    syncDraftsFromPayload();
  }
});

function markExerciseNoteDirty(item: RoutineItem) {
  const draft = ensureExerciseNoteDraft(item);
  draft.dirty = normalizedExerciseNote(draft.value) !== normalizedExerciseNote(draft.baseline);
}

function exerciseNoteValue(item: RoutineItem) {
  return exerciseNoteDrafts[item.id]?.value ?? "";
}

function exerciseNoteBusy(item: RoutineItem) {
  return exerciseNoteDrafts[item.id]?.busy ?? false;
}

function exerciseNoteStatus(item: RoutineItem) {
  const draft = exerciseNoteDrafts[item.id];
  return draft?.dirty ? "Unsaved" : draft?.baseline ? "Saved" : "Add";
}

function onExerciseNoteInput(item: RoutineItem, event: Event) {
  ensureExerciseNoteDraft(item).value = (event.target as HTMLTextAreaElement).value;
  markExerciseNoteDirty(item);
}

function openExerciseNote(item: RoutineItem) {
  ensureExerciseNoteDraft(item);
  noteEditorItem.value = item;
}

function closeExerciseNoteEditor() {
  if (noteEditorItem.value && exerciseNoteBusy(noteEditorItem.value)) return;
  noteEditorItem.value = null;
}

function updateSessionExerciseNote(note: ExerciseFeedback) {
  if (!session.value) return;
  session.value.exercise_notes ??= [];
  const index = session.value.exercise_notes.findIndex((entry) => entry.routine_item_id === note.routine_item_id);
  if (index >= 0) session.value.exercise_notes.splice(index, 1, note);
  else session.value.exercise_notes.push(note);
}

async function saveExerciseNote(item: RoutineItem, closeAfterSave = true): Promise<boolean> {
  if (!session.value) return false;
  const requestSessionId = session.value.id;
  const draft = ensureExerciseNoteDraft(item);
  if (draft.busy) return false;
  const submittedValue = draft.value;
  const submittedNote = normalizedExerciseNote(submittedValue);
  const submittedRevision = draft.revision;
  if (!draft.dirty) {
    if (closeAfterSave) noteEditorItem.value = null;
    return true;
  }
  draft.busy = true;
  try {
    const { note } = await api.saveExerciseNote(requestSessionId, item.id, { notes: submittedNote, expected_revision: submittedRevision });
    if (session.value?.id !== requestSessionId) return false;
    updateSessionExerciseNote(note);
    draft.baseline = note.notes ?? "";
    draft.revision = note.revision;
    if (draft.value === submittedValue) draft.value = note.notes ?? "";
    draft.dirty = normalizedExerciseNote(draft.value) !== normalizedExerciseNote(draft.baseline);
    if (closeAfterSave && !draft.dirty) noteEditorItem.value = null;
    emit("changed");
    emit("toast", submittedNote === null ? "Exercise note cleared" : "Exercise note saved");
    return !draft.dirty;
  } catch (caught) {
    emit("error", caught instanceof Error ? caught.message : "Could not save exercise note");
    return false;
  } finally {
    draft.busy = false;
  }
}

async function clearExerciseNote(item: RoutineItem) {
  const draft = ensureExerciseNoteDraft(item);
  draft.value = "";
  markExerciseNoteDirty(item);
  await saveExerciseNote(item);
}

async function savePendingExerciseNotes(): Promise<boolean> {
  if (!day.value) return true;
  for (const block of day.value.blocks) for (const item of block.items) {
    const draft = exerciseNoteDrafts[item.id];
    if (draft?.dirty && !(await saveExerciseNote(item, false))) return false;
  }
  return true;
}

function markDraftDirty(item: RoutineItem, round: number) {
  const draft = drafts[draftKey(item.id, round)];
  if (draft) { draft.saved = false; draft.dirty = true; }
}

function setResistanceKind(item: RoutineItem, value: ResistanceKind) {
  for (let round = 1; round <= item.target_sets; round += 1) {
    const draft = drafts[draftKey(item.id, round)];
    if (draft && !draft.saved) { draft.resistanceKind = value; draft.saved = false; draft.dirty = true; }
  }
}

function onResistanceChange(item: RoutineItem, event: Event) {
  const value = (event.target as HTMLSelectElement).value;
  setResistanceKind(item, ["external_load", "band", "bodyweight", "assistance", "unknown"].includes(value) ? value as ResistanceKind : "unknown");
}

function resistanceKind(item: RoutineItem): ResistanceKind {
  for (let round = 1; round <= item.target_sets; round += 1) {
    const draft = drafts[draftKey(item.id, round)];
    if (draft && !draft.saved) return draft.resistanceKind;
  }
  return drafts[draftKey(item.id, 1)]?.resistanceKind ?? "unknown";
}

async function start() {
  if (!day.value) return;
  starting.value = true;
  try { await api.startSession(day.value.id); emit("changed"); }
  catch (caught) { emit("error", caught instanceof Error ? caught.message : "Could not start workout"); }
  finally { starting.value = false; }
}

function clearTimer() {
  if (timer) window.clearInterval(timer);
  timer = undefined;
  activeTimer.value = null;
}

async function primeAudio() {
  try {
    audioContext ??= new AudioContext();
    if (audioContext.state === "suspended") await audioContext.resume();
  } catch {
    audioContext = null;
  }
}

function playTone(frequency: number, duration = 0.09, delay = 0) {
  if (!audioContext || audioContext.state !== "running") return;
  const startsAt = audioContext.currentTime + delay;
  const oscillator = audioContext.createOscillator();
  const gain = audioContext.createGain();
  oscillator.frequency.setValueAtTime(frequency, startsAt);
  gain.gain.setValueAtTime(0.0001, startsAt);
  gain.gain.exponentialRampToValueAtTime(0.12, startsAt + 0.012);
  gain.gain.exponentialRampToValueAtTime(0.0001, startsAt + duration);
  oscillator.connect(gain).connect(audioContext.destination);
  oscillator.start(startsAt);
  oscillator.stop(startsAt + duration + 0.01);
}

function completionBeeps() {
  playTone(880, 0.12);
  playTone(1175, 0.16, 0.17);
}

function tickTimer() {
  const current = activeTimer.value;
  if (!current) return;
  const now = Date.now();
  if (now < current.startsAt) {
    const leadInRemaining = Math.max(1, Math.ceil((current.startsAt - now) / 1000));
    if (leadInRemaining !== current.leadInRemaining) {
      current.leadInRemaining = leadInRemaining;
      playTone(659, 0.11);
    }
    return;
  }
  if (current.leadInRemaining > 0) {
    current.leadInRemaining = 0;
    playTone(1047, 0.17);
  }
  const remaining = Math.max(0, Math.ceil((current.endsAt - now) / 1000));
  if (remaining === current.remaining) return;
  current.remaining = remaining;
  if (remaining > 0 && remaining <= 3) playTone(784);
  if (remaining > 0) return;
  const completed = { ...current };
  clearTimer();
  completionBeeps();
  if (completed.kind === "exercise" && completed.block && completed.item && completed.round && completed.duration) {
    void save(completed.block, completed.item, completed.round, completed.duration);
  }
}

function beginTimer(next: Omit<ActiveTimer, "startsAt" | "endsAt" | "remaining" | "leadInRemaining"> & { duration: number }, leadInSeconds = 0) {
  clearTimer();
  const now = Date.now();
  activeTimer.value = {
    ...next,
    startsAt: now + leadInSeconds * 1000,
    endsAt: now + (leadInSeconds + next.duration) * 1000,
    remaining: next.duration,
    leadInRemaining: leadInSeconds
  };
  if (leadInSeconds > 0) playTone(659, 0.11);
  timer = window.setInterval(tickTimer, 200);
}

function startRest(seconds: number) {
  if (seconds <= 0) return;
  beginTimer({ kind: "rest", duration: seconds });
}

async function toggleExerciseTimer(block: RoutineBlock, item: RoutineItem, round: number) {
  if (isExerciseTimer(item, round)) {
    clearTimer();
    return;
  }
  const duration = Number(ensureDraft(item, round).duration);
  if (!Number.isInteger(duration) || duration < 1 || duration > 3_600) {
    emit("error", "Enter a timer duration between 1 and 3600 seconds");
    return;
  }
  await primeAudio();
  beginTimer({ kind: "exercise", duration, block, item, round }, TIMER_LEAD_IN_SECONDS);
}

async function save(block: RoutineBlock, item: RoutineItem, round: number, completedDuration?: number) {
  if (!session.value) return;
  void primeAudio();
  if (isExerciseTimer(item, round)) clearTimer();
  const draft = ensureDraft(item, round);
  draft.busy = true;
  try {
    const result = await api.logSet(session.value.id, {
      routine_item_id: item.id, exercise_id: item.exercise.id, round_number: round,
      weight: draft.weight === "" ? undefined : Number(draft.weight), reps: draft.reps === "" ? undefined : Number(draft.reps),
      duration_seconds: completedDuration,
      rir: draft.rir === "" ? undefined : Number(draft.rir), resistance_kind: draft.resistanceKind
    });
    const existing = session.value.sets.findIndex((set) => set.routine_item_id === item.id && set.round_number === round);
    if (existing >= 0) session.value.sets.splice(existing, 1, result.set); else session.value.sets.push(result.set);
    draft.saved = true;
    draft.dirty = false;
    if (item.position === block.items.length - 1) startRest(block.rest_seconds);
    // Supersede any resume read captured before this set was saved.
    emit("changed");
  } catch (caught) { emit("error", caught instanceof Error ? caught.message : "Could not save set"); }
  finally { draft.busy = false; }
}

async function finish() {
  if (!session.value) return;
  finishing.value = true;
  try {
    if (!(await savePendingExerciseNotes())) return;
    await api.completeSession(session.value.id, { perceived_effort: effort.value, notes: notes.value || undefined });
    clearTimer();
    finishOpen.value = false;
    emit("toast", "Workout complete. Strong work.");
    emit("changed");
  } catch (caught) { emit("error", caught instanceof Error ? caught.message : "Could not finish workout"); }
  finally { finishing.value = false; }
}

async function abandonCurrent() {
  if (!session.value) return;
  abandoning.value = true;
  try {
    if (!(await savePendingExerciseNotes())) return;
    await api.abandonSession(session.value.id);
    abandonOpen.value = false;
    emit("toast", "Workout abandoned");
    emit("changed");
  } catch (caught) { emit("error", caught instanceof Error ? caught.message : "Could not abandon workout"); }
  finally { abandoning.value = false; }
}

function target(item: RoutineItem) {
  if (item.duration_seconds) return `${item.target_sets} × ${formatDuration(item.duration_seconds)}${item.target_rir !== null ? ` · ${item.target_rir} RIR` : ""}`;
  const reps = item.rep_min && item.rep_max ? `${item.rep_min}–${item.rep_max} reps` : item.rep_min ? `${item.rep_min}+ reps` : "quality reps";
  return `${item.target_sets} × ${reps}${item.target_rir !== null ? ` · ${item.target_rir} RIR` : ""}`;
}

function previous(item: RoutineItem) {
  if (!item.previous) return "No prior set";
  const weight = item.previous.weight !== null ? `${item.previous.weight} lb` : "bodyweight";
  if (item.previous.duration_seconds !== null) return `${weight} · ${formatDuration(item.previous.duration_seconds)}${item.previous.rir !== null ? ` @ ${item.previous.rir}` : ""}`;
  return `${weight} × ${item.previous.reps ?? "—"}${item.previous.rir !== null ? ` @ ${item.previous.rir}` : ""}`;
}

function previousNoteLabel(item: RoutineItem) {
  if (!item.previous_note?.session_completed_at) return "Previous note";
  return `Previous note · ${new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric" }).format(new Date(item.previous_note.session_completed_at))}`;
}

function formatDuration(seconds: number) {
  const minutes = Math.floor(seconds / 60);
  return `${minutes}:${String(seconds % 60).padStart(2, "0")}`;
}

function isExerciseTimer(item: RoutineItem, round: number) {
  return activeTimer.value?.kind === "exercise" && activeTimer.value.item?.id === item.id && activeTimer.value.round === round;
}

function timedDisplay(item: RoutineItem, round: number) {
  if (!isExerciseTimer(item, round)) return "";
  return activeTimer.value!.leadInRemaining > 0 ? `${activeTimer.value!.leadInRemaining}` : formatDuration(activeTimer.value!.remaining);
}

function timedButtonAria(item: RoutineItem, round: number) {
  if (isExerciseTimer(item, round)) {
    const current = activeTimer.value!;
    return current.leadInRemaining > 0
      ? `Cancel timer for ${item.exercise.name} set ${round}, starting in ${current.leadInRemaining} seconds`
      : `Cancel timer for ${item.exercise.name} set ${round}, ${current.remaining} seconds remaining`;
  }
  return `Start ${ensureDraft(item, round).duration || 0} second timer for ${item.exercise.name} set ${round}`;
}

const restRemaining = computed(() => activeTimer.value?.kind === "rest" ? activeTimer.value.remaining : 0);

onBeforeUnmount(() => {
  clearTimer();
  if (audioContext) void audioContext.close();
});
</script>

<template>
  <section class="view train-view">
    <div v-if="loading" class="skeleton-stack" aria-label="Loading workout"><i></i><i></i><i></i></div>
    <section v-else-if="detached" class="empty-state detached-session" aria-live="polite">
      <span class="empty-glyph">!</span><h2>Workout needs recovery</h2>
      <p>Your active workout “{{ session?.title }}” is not available from the current plan. It is still active and has not been abandoned automatically.</p>
      <p class="muted">Refresh the plan or ask your coach to reconnect it. If you are finished, abandon this workout deliberately.</p>
      <button class="button secondary" @click="abandonOpen = true">Abandon workout</button>
    </section>
    <div v-else-if="!day" class="empty-state">
      <span class="empty-glyph">＋</span><h2>No workout planned</h2><p>Ask your agent to populate the week, or use the agent API guide in Plan.</p>
    </div>
    <template v-else>
      <header class="workout-hero">
        <div>
          <p class="eyebrow"><span v-if="payload?.relation === 'next'">Up next · </span>{{ dateLabel }}</p>
          <h1>{{ day.title }}</h1>
          <p>{{ day.focus }}</p>
        </div>
        <div class="workout-stat"><strong>{{ day.estimated_minutes ?? "—" }}</strong><span>MIN</span></div>
      </header>

      <div v-if="day.warmup.length" class="warmup-note"><span>WARM-UP</span><ul><li v-for="step in day.warmup" :key="step">{{ step }}</li></ul></div>
      <div class="coach-note" v-if="day.coach_notes"><span>COACH</span><p>{{ day.coach_notes }}</p></div>

      <div v-if="!session" class="preflight card">
        <div class="preflight-row"><span>{{ day.blocks.length }} blocks</span><span>{{ totalSets }} working sets</span><span>{{ day.blocks.filter(b => b.kind === 'superset').length }} supersets</span></div>
        <button class="button primary full" :disabled="starting" @click="start">{{ starting ? "Starting…" : payload?.relation === "next" ? "Start early" : "Start workout" }}</button>
      </div>

      <template v-else>
        <div class="session-progress"><span :style="{ width: `${progress}%` }"></span></div>
        <div class="session-meta"><b>{{ completedSets }} / {{ totalSets }} sets</b><span>Started {{ new Date(session.started_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) }}</span></div>
      </template>

      <article v-for="block in day.blocks" :key="block.id" class="block-card" :class="{ dormant: !session }">
        <header class="block-header">
          <div><span class="block-label">{{ block.label }}</span><div><p class="eyebrow">{{ block.kind }} · {{ block.rounds }} rounds</p><h2>{{ block.items.map(item => item.exercise.name).join(" + ") }}</h2></div></div>
          <span class="rest-chip">{{ block.rest_seconds }}s rest</span>
        </header>
        <p v-if="block.notes" class="block-note">{{ block.notes }}</p>
        <div class="paired-grid">
          <section v-for="item in block.items" :key="item.id" class="exercise-card">
            <header>
              <span class="item-code">{{ block.label }}{{ item.position + 1 }}</span>
              <div><h3>{{ item.exercise.name }}</h3><p>{{ target(item) }}</p></div>
              <a v-if="item.exercise.video_url" :href="item.exercise.video_url" target="_blank" rel="noopener" aria-label="Open technique video">↗</a>
            </header>
            <div class="previous-line"><span>LAST</span><b>{{ previous(item) }}</b></div>
            <p class="exercise-note">{{ item.notes ?? "" }}</p>
            <div v-if="session" class="set-grid">
              <label class="resistance-control">Resistance<select :value="resistanceKind(item)" :aria-label="`Resistance for ${item.exercise.name}`" @change="onResistanceChange(item, $event)"><option value="unknown">Unknown</option><option value="external_load">External load</option><option value="band">Band</option><option value="bodyweight">Bodyweight</option><option value="assistance">Assistance</option></select></label>
              <div class="set-head"><span>SET</span><span>LB</span><span>{{ item.duration_seconds ? "TIME" : "REPS" }}</span><span>RIR</span><span></span></div>
              <div v-for="round in item.target_sets" :key="round" class="set-row" :class="{ saved: ensureDraft(item, round).saved }">
                <b>{{ round }}</b>
                <input v-model="ensureDraft(item, round).weight" inputmode="decimal" aria-label="Weight" @input="markDraftDirty(item, round)" />
                <div v-if="item.duration_seconds" class="timed-field" :class="{ active: isExerciseTimer(item, round) }">
                  <output v-if="isExerciseTimer(item, round)" aria-live="polite">{{ timedDisplay(item, round) }}</output>
                  <input
                    v-else
                    v-model="ensureDraft(item, round).duration"
                    inputmode="numeric"
                    pattern="[0-9]*"
                    :aria-label="`Timer seconds for ${item.exercise.name} set ${round}`"
                    @input="markDraftDirty(item, round)"
                    @keydown.enter.prevent="toggleExerciseTimer(block, item, round)"
                  />
                </div>
                <input v-else v-model="ensureDraft(item, round).reps" inputmode="numeric" aria-label="Reps" @input="markDraftDirty(item, round)" />
                <input v-model="ensureDraft(item, round).rir" inputmode="decimal" aria-label="Reps in reserve" @input="markDraftDirty(item, round)" />
                <button
                  v-if="item.duration_seconds"
                  class="timer-trigger"
                  :class="{ active: isExerciseTimer(item, round) }"
                  :disabled="ensureDraft(item, round).busy"
                  :aria-label="timedButtonAria(item, round)"
                  @click="toggleExerciseTimer(block, item, round)"
                >
                  {{ isExerciseTimer(item, round) ? "■" : ensureDraft(item, round).saved ? "↻" : "▶" }}
                </button>
                <button v-else :disabled="ensureDraft(item, round).busy" :aria-label="`Save set ${round} for ${item.exercise.name}`" @click="save(block, item, round)">{{ ensureDraft(item, round).saved ? "✓" : "＋" }}</button>
              </div>
            </div>
            <div class="exercise-extras">
              <button v-if="session" type="button" class="exercise-note-trigger" :class="{ dirty: exerciseNoteStatus(item) === 'Unsaved' }" :aria-label="`Edit your note for ${item.exercise.name}`" @click="openExerciseNote(item)">
                <span>Your note</span><b>{{ exerciseNoteStatus(item) }}</b>
              </button>
              <details v-if="item.previous_note?.notes" class="previous-exercise-note">
                <summary>{{ previousNoteLabel(item) }}</summary><p>{{ item.previous_note.notes }}</p>
              </details>
              <details v-if="item.exercise.cues.length"><summary>Technique cues</summary><ul><li v-for="cue in item.exercise.cues" :key="cue">{{ cue }}</li></ul></details>
            </div>
          </section>
        </div>
      </article>

      <button v-if="session" class="button finish-button" @click="finishOpen = true">Finish workout</button>
      <button v-if="session" class="button ghost abandon-button" @click="abandonOpen = true">Abandon workout</button>
    </template>

    <button v-if="restRemaining" class="rest-timer" aria-label="Skip rest timer" @click="clearTimer"><span>REST</span><strong>{{ formatDuration(restRemaining) }}</strong><small>tap to skip</small></button>

    <div v-if="noteEditorItem && session" class="modal-backdrop" @click.self="closeExerciseNoteEditor" @keydown.esc="closeExerciseNoteEditor">
      <form class="modal exercise-note-modal" role="dialog" aria-modal="true" aria-labelledby="exercise-note-title" @submit.prevent="saveExerciseNote(noteEditorItem)">
        <p class="eyebrow">Your note</p><h2 id="exercise-note-title">{{ noteEditorItem.exercise.name }}</h2>
        <p class="muted">Setup, substitutions, or how it felt. Saved with this workout.</p>
        <label>Exercise feedback<textarea :value="exerciseNoteValue(noteEditorItem)" rows="5" maxlength="1000" placeholder="What felt useful, awkward, strong, or worth changing next time?" autofocus @input="onExerciseNoteInput(noteEditorItem, $event)"></textarea></label>
        <div class="exercise-note-count">{{ exerciseNoteValue(noteEditorItem).length }} / 1000</div>
        <button class="button primary full" :disabled="exerciseNoteBusy(noteEditorItem) || exerciseNoteStatus(noteEditorItem) !== 'Unsaved'">{{ exerciseNoteBusy(noteEditorItem) ? "Saving…" : "Save note" }}</button>
        <button v-if="exerciseNoteStatus(noteEditorItem) !== 'Add' || exerciseNoteValue(noteEditorItem)" type="button" class="button ghost full" :disabled="exerciseNoteBusy(noteEditorItem)" @click="clearExerciseNote(noteEditorItem)">Clear note</button>
        <button type="button" class="button ghost full" :disabled="exerciseNoteBusy(noteEditorItem)" @click="closeExerciseNoteEditor">Close</button>
      </form>
    </div>

    <div v-if="finishOpen" class="modal-backdrop" @click.self="finishOpen = false">
      <form class="modal" @submit.prevent="finish">
        <p class="eyebrow">Session complete</p><h2>How did it feel?</h2>
        <div class="effort-value"><strong>{{ effort }}</strong><span>/ 10 effort</span></div>
        <input v-model.number="effort" type="range" min="1" max="10" />
        <label>Notes<textarea v-model="notes" rows="3" placeholder="Energy, pain, wins, changes for next time…"></textarea></label>
        <button class="button primary full" :disabled="finishing">{{ finishing ? "Saving…" : "Complete workout" }}</button>
        <button type="button" class="button ghost full" @click="finishOpen = false">Keep training</button>
      </form>
    </div>
    <div v-if="abandonOpen" class="modal-backdrop" @click.self="abandonOpen = false">
      <div class="modal" role="dialog" aria-modal="true" aria-labelledby="abandon-title">
        <p class="eyebrow">Active workout</p><h2 id="abandon-title">Abandon this workout?</h2>
        <p>This removes the active workout from your queue without creating completed history. Any sets already saved remain recorded.</p>
        <button class="button primary full" :disabled="abandoning" @click="abandonCurrent">{{ abandoning ? "Abandoning…" : "Yes, abandon workout" }}</button>
        <button class="button ghost full" :disabled="abandoning" @click="abandonOpen = false">Keep workout active</button>
      </div>
    </div>
  </section>
</template>
